// =============================================================
//  AdminVerificationAnnonceScreen — Liste des annonces à vérifier
// =============================================================

import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';

import AdminLayout from './web-ui/AdminLayout';
import {
  Card,
  Button,
  Badge,
  EmptyState,
  Loading,
  DataRow,
  SegmentedSelect,
  SearchBar,
  StatCard,
} from './web-ui/kit';
import { colors, spacing } from './web-ui/theme';

const FILTERS = [
  { value: 'pending', label: 'À vérifier' },
  { value: 'approved', label: 'Approuvées' },
  { value: 'rejected', label: 'Rejetées' },
  { value: 'all', label: 'Toutes' },
];

const stateVariant = (e) => {
  const v = String(e || '').toLowerCase();
  if (['approved', 'active'].includes(v)) return 'success';
  if (['pending', 'verification', 'en-cours'].includes(v)) return 'warning';
  if (['rejected', 'cancelled'].includes(v)) return 'danger';
  return 'neutral';
};

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('fr-FR');
};

export default function AdminVerificationAnnonceScreen({ navigation }) {
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      let snap;
      try {
        snap = await getDocs(query(collection(db, 'announcements'), orderBy('createdAt', 'desc')));
      } catch {
        snap = await getDocs(collection(db, 'announcements'));
      }
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.log('verification load error', e?.message || e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = items.filter((a) => {
    const state = String(a.etat || a.status || 'pending').toLowerCase();
    if (filter !== 'all') {
      if (filter === 'pending' && !['pending', 'verification', 'en-cours'].includes(state)) return false;
      if (filter === 'approved' && !['approved', 'active'].includes(state)) return false;
      if (filter === 'rejected' && !['rejected', 'cancelled'].includes(state)) return false;
    }
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (a.title || a.titre || '').toLowerCase().includes(q);
  });

  const stats = {
    pending: items.filter((a) =>
      ['pending', 'verification', 'en-cours'].includes(String(a.etat || a.status || 'pending').toLowerCase())
    ).length,
    approved: items.filter((a) =>
      ['approved', 'active'].includes(String(a.etat || a.status || '').toLowerCase())
    ).length,
    rejected: items.filter((a) =>
      ['rejected', 'cancelled'].includes(String(a.etat || a.status || '').toLowerCase())
    ).length,
  };

  return (
    <AdminLayout
      title="Vérification d'annonces"
      subtitle="Examiner et approuver les annonces soumises"
    >
      <View style={styles.statsRow}>
        <StatCard label="En attente" value={stats.pending} icon="clock-outline" accent={colors.warning} loading={loading} />
        <StatCard label="Approuvées" value={stats.approved} icon="check-circle-outline" accent={colors.success} loading={loading} />
        <StatCard label="Rejetées" value={stats.rejected} icon="close-circle-outline" accent={colors.danger} loading={loading} />
      </View>

      <Card style={{ marginBottom: spacing.lg }}>
        <SegmentedSelect options={FILTERS} value={filter} onChange={setFilter} />
      </Card>

      <View style={styles.toolbar}>
        <View style={{ flex: 1, minWidth: 240 }}>
          <SearchBar value={search} onChangeText={setSearch} placeholder="Rechercher..." />
        </View>
      </View>

      <Card noPadding>
        {loading ? (
          <Loading label="Chargement..." />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="shield-check-outline"
            title="Aucune annonce à vérifier"
            subtitle="Toutes les annonces sont traitées 👍"
          />
        ) : (
          filtered.map((a, i) => {
            const title = a.title || a.titre || 'Sans titre';
            return (
              <DataRow
                key={a.id}
                last={i === filtered.length - 1}
                onPress={() =>
                  navigation.navigate('AdminVerificationAnnonceDetailScreen', { announcement: a })
                }
              >
                <View
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    backgroundColor: colors.warningSoft,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text style={{ color: colors.warning, fontWeight: '700', fontSize: 16 }}>
                    {title.charAt(0).toUpperCase()}
                  </Text>
                </View>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.rowTitle} numberOfLines={1}>{title}</Text>
                  <Text style={styles.rowSubtitle} numberOfLines={1}>
                    {a.centerName || a.author || '—'} · {fmtDate(a.createdAt)}
                  </Text>
                </View>
                <Badge variant={stateVariant(a.etat || a.status)}>
                  {a.etat || a.status || 'pending'}
                </Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  icon="arrow-right"
                  onPress={() =>
                    navigation.navigate('AdminVerificationAnnonceDetailScreen', { announcement: a })
                  }
                />
              </DataRow>
            );
          })
        )}
      </Card>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  statsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.lg, marginBottom: spacing.lg },
  toolbar: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, marginBottom: spacing.lg },
  rowTitle: { fontSize: 14, fontWeight: '600', color: colors.text },
  rowSubtitle: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
});
